///
/// EarthBrowser - File Drop Zone
///
/// Handles drag and drop functionality for CSV, Shapefiles, GeoPackages, and folders
/// Created by Matt Giger on December 15, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useAppDispatch, useAppStore } from '@/redux/store'
import type { GeoPackageCatalog } from '@shared-types/geopackage-catalog'
import { track } from '@/utils/analytics'
import { useCallback, useEffect, useRef, useState } from "react"
import { CSVFileHandler, GeoJSONFileHandler, GeoPackageFileHandler, GeoTiffFileHandler, ImageFileHandler, KMLFileHandler, ShapefileFileHandler } from './handlers'

export interface GeoPackageImportedEvent {
	package: GeoPackageCatalog
}

declare global {
	interface WindowEventMap {
		'geopackage:imported': CustomEvent<GeoPackageImportedEvent>
	}

	// Webkit directory API types
	interface DataTransferItem {
		webkitGetAsEntry(): FileSystemEntry | null
		getAsFileSystemHandle?(): Promise<FileSystemHandle>
	}

	interface FileSystemEntry {
		readonly isDirectory: boolean
		readonly isFile: boolean
		readonly name: string
	}
}

interface FileDropZoneProps {
	children: React.ReactNode
	className?: string
}

export function FileDropZone({ children, className }: FileDropZoneProps) {
	const dispatch = useAppDispatch()
	const store = useAppStore()
	const [isDragActive, setIsDragActive] = useState(false)
	const csvHandlerRef = useRef<CSVFileHandler | null>(null)
	const shapefileHandlerRef = useRef<ShapefileFileHandler | null>(null)
	const geojsonHandlerRef = useRef<GeoJSONFileHandler | null>(null)
	const geopackageHandlerRef = useRef<GeoPackageFileHandler | null>(null)
	const imageHandlerRef = useRef<ImageFileHandler | null>(null)
	const geotiffHandlerRef = useRef<GeoTiffFileHandler | null>(null)
	const kmlHandlerRef = useRef<KMLFileHandler | null>(null)

	// Initialize handlers
	useEffect(() => {
		const context = {
			dispatch,
			getState: () => store.getState()
		}

		csvHandlerRef.current = new CSVFileHandler(context)
		csvHandlerRef.current.initialize()

		shapefileHandlerRef.current = new ShapefileFileHandler(context)
		shapefileHandlerRef.current.initialize()

		geojsonHandlerRef.current = new GeoJSONFileHandler(context)
		geojsonHandlerRef.current.initialize()

		geopackageHandlerRef.current = new GeoPackageFileHandler(context)
		geopackageHandlerRef.current.initialize()

		imageHandlerRef.current = new ImageFileHandler(context)
		imageHandlerRef.current.initialize()

		geotiffHandlerRef.current = new GeoTiffFileHandler(context)
		geotiffHandlerRef.current.initialize()

		kmlHandlerRef.current = new KMLFileHandler(context)
		kmlHandlerRef.current.initialize()

		return () => {
			csvHandlerRef.current?.cleanup()
			shapefileHandlerRef.current?.cleanup()
			geojsonHandlerRef.current?.cleanup()
			geopackageHandlerRef.current?.cleanup()
			imageHandlerRef.current?.cleanup()
			geotiffHandlerRef.current?.cleanup()
			kmlHandlerRef.current?.cleanup()
		}
	}, [dispatch, store])


	// Handle file drop
	const handleDrop = useCallback(async (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragActive(false)

		// Check if we have directory entries (folder drop)
		const items = Array.from(e.dataTransfer.items)
		let processedDirectory = false

		// Try to get directory handles first (modern API)
		if (items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
			// Use the older webkitGetAsEntry API for better compatibility
			const entry = items[0].webkitGetAsEntry()
			if (entry?.isDirectory) {
				// We have a directory, try the modern API
				for (const item of items) {
					if (item.kind === 'file') {
						try {
							// Try the modern File System Access API
							const handle = await item.getAsFileSystemHandle?.()
							if (handle?.kind === 'directory') {
								processedDirectory = true
								// Process directory using shapefile handler
								const dirHandle = handle as FileSystemDirectoryHandle

								if (shapefileHandlerRef.current) {
									await shapefileHandlerRef.current.handleDirectory(dirHandle)
								} else {
									console.error('Shapefile handler not initialized')
								}

								return // Processed directory, done
							}
						} catch {
							// Fall through to regular file handling
							console.log('Directory API not available or failed, falling back to file handling')
						}
					}
				}
			}
		}

		// Skip regular file handling if we already processed a directory
		if (processedDirectory) {
			return
		}

		// Regular file drop handling
		const files = e.dataTransfer.files
		if (files && files.length > 0) {
			const file = files[0]

			// Find the appropriate handler for this file
			const handlers = [
				csvHandlerRef.current,
				shapefileHandlerRef.current,
				geojsonHandlerRef.current,
				geopackageHandlerRef.current,
				imageHandlerRef.current,
				geotiffHandlerRef.current,
				kmlHandlerRef.current
			]

			const handler = handlers.find(h => h?.canHandle(file))
			const format = (file.name.split('.').pop() ?? '').toLowerCase().slice(0, 8) || 'unknown'
			const size_bucket = file.size < 100_000 ? 'xs' : file.size < 1_000_000 ? 's'
				: file.size < 10_000_000 ? 'm' : file.size < 100_000_000 ? 'l' : 'xl'

			if (handler) {
				try {
					await handler.handleFile(file)
					track('file_imported', { format, size_bucket, ok: true })
				} catch (error) {
					console.error(`Failed to process file ${file.name}:`, error)
					track('file_imported', { format, size_bucket, ok: false })
				}
			} else {
				console.warn(`No handler found for file: ${file.name}`)
				track('file_imported', { format, size_bucket, ok: false })
			}
		}
	}, [])

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragActive(true)
	}, [])

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragActive(false)
	}, [])

	return (
		<div
			className={className}
			onDrop={handleDrop}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
		>
		{children}
		{isDragActive && (
			<div className="absolute inset-0 bg-cyan-500/10 border-4 border-dashed border-cyan-400 pointer-events-none flex items-center justify-center">
				<div className="bg-black/80 backdrop-blur-sm px-6 py-4 rounded-lg border border-cyan-400/50">
					<p className="text-cyan-400 font-semibold text-lg">Drop file or folder to import</p>
					<p className="text-cyan-400/70 text-sm mt-1">CSV, GeoJSON, Shapefile (.shp/.zip/folder), KML/KMZ → Data browser</p>
					<p className="text-cyan-400/70 text-sm">Image, GeoTIFF (.tif) → Globe overlay · GeoPackage (.gpkg) → Library</p>
				</div>
			</div>
		)}
		</div>
	)
}

